import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Waves, FlaskConical, TrendingUp, Calendar } from 'lucide-react';
import { getReadingStatus } from '../utils/complianceRanges';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalPools: 0,
    totalReadings: 0,
    recentReadings: [],
    complianceRate: 0,
    poolLimit: null
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [poolsRes, readingsRes, poolCountRes] = await Promise.all([
        axios.get(`${API}/pools`),
        axios.get(`${API}/readings`),
        axios.get(`${API}/pools/count`)
      ]);
      
      // Calculate compliance rate
      const compliantReadings = readingsRes.data.filter(reading => 
        getReadingStatus(reading).compliant
      ).length;
      const complianceRate = readingsRes.data.length > 0 
        ? Math.round((compliantReadings / readingsRes.data.length) * 100)
        : 100;
      
      setStats({
        totalPools: poolsRes.data.length,
        totalReadings: readingsRes.data.length,
        recentReadings: readingsRes.data.slice(0, 5),
        complianceRate,
        poolLimit: poolCountRes.data.limit
      });
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="text-4xl font-bold text-secondary tracking-tight">{t('dashboard')}</h1>
        <p className="text-muted-foreground mt-2">Pool compliance overview and quick actions</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card data-testid="total-pools-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {stats.poolLimit ? 'Your Pools' : 'Total Pools'}
            </CardTitle>
            <Waves className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono text-primary">
              {stats.poolLimit ? `${stats.totalPools}/${stats.poolLimit}` : stats.totalPools}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.poolLimit ? 'Pools allocated' : 'Active pool profiles'}
            </p>
          </CardContent>
        </Card>

        <Card data-testid="total-readings-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Readings</CardTitle>
            <FlaskConical className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono text-primary">{stats.totalReadings}</div>
            <p className="text-xs text-muted-foreground mt-1">Chemical measurements recorded</p>
          </CardContent>
        </Card>

        <Card data-testid="compliance-rate-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Compliance Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold font-mono ${stats.complianceRate >= 90 ? 'text-success' : stats.complianceRate >= 70 ? 'text-warning' : 'text-danger'}`}>
              {stats.complianceRate}%
            </div>
            <p className="text-xs text-muted-foreground mt-1">Within acceptable ranges</p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Common tasks and shortcuts</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Button
              data-testid="add-pool-button"
              onClick={() => navigate('/pools')}
              className="h-auto py-6 flex flex-col items-center space-y-2"
            >
              <Waves className="h-6 w-6" />
              <span>{t('addPool')}</span>
            </Button>
            <Button
              data-testid="add-reading-button"
              onClick={() => navigate('/pools')}
              className="h-auto py-6 flex flex-col items-center space-y-2"
            >
              <FlaskConical className="h-6 w-6" />
              <span>{t('addReading')}</span>
            </Button>
            <Button
              data-testid="view-pools-button"
              onClick={() => navigate('/pools')}
              className="h-auto py-6 flex flex-col items-center space-y-2"
              variant="outline"
            >
              <Calendar className="h-6 w-6" />
              <span>{t('pools')}</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Readings</CardTitle>
          <CardDescription>Latest chemical measurements</CardDescription>
        </CardHeader>
        <CardContent>
          {stats.recentReadings.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">{t('noData')}</p>
          ) : (
            <div className="space-y-4">
              {stats.recentReadings.map((reading) => (
                <div key={reading.id} className="flex items-center justify-between border-b pb-3">
                  <div>
                    <p className="font-medium">Pool ID: {reading.pool_id.substring(0, 8)}...</p>
                    <p className="text-sm text-muted-foreground">
                      pH: {reading.ph} | Chlorine: {reading.chlorine} ppm
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">
                      {new Date(reading.date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;